import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector }        from 'react-redux';
import {
  setInProgress,
  clearInProgress,
  addPath,
  updatePoint,
  updateSnapPoint,
  removePath,
  removePathOnly,
  removePointFromPath,
  selectPath,
  selectPoint,
  clearNavSelection,
  setEraserSelectedPoints,
  clearEraserSelectedPoints,
  bulkAddPaths,
  insertPointAtIndex,
  updatePathPoints,
} from '../../../../../store/slices/navigationSlice';
import {
  findSnap,
  getPositionAtT,
  nearestOnPolyline2D,
  nodesToGeoJSON,
  pathsToLinesGeoJSON,
  previewToGeoJSON,
  emptyCol,
  makePoint,
  buildNavGraph,
  dijkstra,
  findPinNodeId,
  autoGenerateSubPaths,
  decomposeSnapT,
  reAnchorSnapPoint,
} from './navigationUtils';
import { navSourceRef, NAV_LAYERS } from './NavigationLayer';
 
const pushLines   = (paths, selId)            => navSourceRef.setLines?.(paths, selId);
const pushNodes   = (paths, selId, selPtId)   => navSourceRef.setNodes?.(paths, selId, selPtId);
const pushPreview = (inProg, mousePos)         => navSourceRef.setPreview?.(inProg, mousePos);

export default function useNavigationManager({ activeTool, activePath }) {
  const dispatch    = useDispatch();
  const map         = useSelector((s) => s.map.mapContainer);
  const allPins     = useSelector((s) => s.api.allPins);
  const currentFloor= useSelector((s) => s.api.currentFloor);
  const activeTab   = useSelector((s) => s.api.activeTab);

  const allPaths        = useSelector((s) => s.navigation.paths);
  const selectedPathId  = useSelector((s) => s.navigation.selectedPathId);
  const selectedPointId = useSelector((s) => s.navigation.selectedPointId);
  const inProgress      = useSelector((s) => s.navigation.inProgress);
  const shortestPath    = useSelector((s) => s.navigation.shortestPath);
 
  const floorId = currentFloor?.enc_id ?? null;
  const paths   = allPaths.filter((p) => p.floorId != null && floorId != null && String(p.floorId) === String(floorId));
 
  const stateRef = useRef({});
  stateRef.current = {
    activeTool, activePath, paths, selectedPathId, selectedPointId, inProgress,
    currentFloor,
  };
 
  const visiblePins = useRef([]);
  useEffect(() => {
    const { vertical, ...rest } = allPins || {};
 
    const flat = Object.values(rest).flat();
    const regularPins = flat.filter((pin) => {
      if (!pin?.positions) return false;
      if (String(pin?.fp_id) !== String(currentFloor?.enc_id)) return false;
      if (activeTab !== 'all' && pin?.category !== activeTab) return false;
      return true;
    });
 
    const verticalPinsOnFloor = (vertical || []).flatMap((vt) => {
      const conn = vt.connections?.find(
        (c) => Number(c.fp_id) === Number(currentFloor?.enc_id)
      );
      if (!conn?.positions) return [];
      return [{
        enc_id:   vt.enc_id,
        positions: conn.positions, 
        fp_id:    currentFloor.enc_id,
        category: 'vertical',
      }];
    });

    visiblePins.current = [...regularPins, ...verticalPinsOnFloor];
  }, [allPins, currentFloor, activeTab]);
 
  const setCursor = useCallback((c) => {
    if (map) map.getCanvas().style.cursor = c;
  }, [map]);
 
  const hitTestPath = useCallback((screenPt) => {
    if (!map) return null;
    const features = map.queryRenderedFeatures(
      [
        [screenPt.x - 8, screenPt.y - 8],
        [screenPt.x + 8, screenPt.y + 8],
      ],
      { layers: [NAV_LAYERS.hitLine, NAV_LAYERS.mainLine, NAV_LAYERS.subLine, NAV_LAYERS.selectedLine] }
    );
    if (features.length === 0) return null; 
    return features[0].properties?.pathId ?? null;
  }, [map]);
 
  const hitTestNode = useCallback((screenPt) => {
    if (!map) return null;
    const features = map.queryRenderedFeatures(
      [
        [screenPt.x - 10, screenPt.y - 10],
        [screenPt.x + 10, screenPt.y + 10],
      ],
      { layers: [NAV_LAYERS.nodes, NAV_LAYERS.nodesSnap] }
    );
    if (features.length === 0) return null;
    const p = features[0].properties;
    if (!p?.pointId || !p?.pathId) return null;
    return { pointId: p.pointId, pathId: p.pathId };
  }, [map]);
 
  // useEffect(() => {
  //   if (!map || !activeTool) return;

  //   const cleanups = [];
  //   const on = (evt, handler) => {
  //     map.on(evt, handler);
  //     cleanups.push(() => map.off(evt, handler));
  //   };
  //   const onKey = (evt, handler) => {
  //     window.addEventListener(evt, handler);
  //     cleanups.push(() => window.removeEventListener(evt, handler));
  //   };

  //   setCursor('default');
  //   dispatch(clearInProgress());
  //   pushPreview(null);

  //   if(shortestPath?.positions) return
 
  //   if (activeTool === 'pen') {
  //     setCursor('crosshair');
 
  //     let localProgress    = null; 
  //     let localMergeTarget = null; 

  //     const commitPath = () => {
  //       if (!localProgress || localProgress.points.length < 2) {
  //         localProgress    = null;
  //         localMergeTarget = null;
  //         dispatch(clearInProgress());
  //         pushPreview(null);
  //         return;
  //       }
 
  //       const mergeTarget = localMergeTarget;
  //       localMergeTarget  = null;

  //       if (mergeTarget) {
  //         const { paths: latestPaths } = stateRef.current;
  //         const targetPath = latestPaths.find((p) => p.id === mergeTarget.pathId);
  //         if (targetPath && targetPath.type === localProgress.type) {
  //           const tFirst = targetPath.points[0];
  //           const tLast  = targetPath.points[targetPath.points.length - 1];
  //           let mergedPoints = null;

  //           if (tFirst?.id === mergeTarget.pointId) { 
  //             mergedPoints = [
  //               ...localProgress.points.slice(0, -1),
  //               ...targetPath.points,
  //             ];
  //           } else if (tLast?.id === mergeTarget.pointId) { 
  //             mergedPoints = [
  //               ...localProgress.points.slice(0, -1),
  //               ...targetPath.points.slice().reverse(),
  //             ];
  //           }

  //           if (mergedPoints && mergedPoints.length >= 2) {
  //             dispatch(addPath({ type: localProgress.type, points: mergedPoints, floorId: stateRef.current.currentFloor?.enc_id }));
  //             dispatch(removePath(targetPath.id));
  //             localProgress = null;
  //             pushPreview(null);
  //             return;
  //           }
  //         }
  //       }

  //       dispatch(addPath({ type: localProgress.type, points: localProgress.points, floorId: stateRef.current.currentFloor?.enc_id }));
  //       localProgress = null;
  //       pushPreview(null);
  //     };

  //     const cancelPath = () => {
  //       localProgress = null;
  //       dispatch(clearInProgress());
  //       pushPreview(null);
  //       setCursor('crosshair');
  //     };
 
  //     on('click', (e) => {
  //       const { lng, lat } = e.lngLat;
  //       const { activePath: curPath, paths: curPaths } = stateRef.current;
 
  //       const snap = findSnap({
  //         lngLat: [lng, lat],
  //         map,
  //         paths:  curPaths,
  //         visiblePins: visiblePins.current,
  //         pinThresholdPx: 25,
  //       });

  //       let newPoint;
  //       let shouldEnd = false;
  //       localMergeTarget = null;  

  //       if (snap) {
  //         if (snap.type === 'pin') { 
  //           newPoint = makePoint(snap.position, { pinId: snap.pinId }); 
  //           if (localProgress !== null) shouldEnd = true;

  //         } else if (snap.type === 'node') {
  //           const snapPath  = curPaths.find((p) => p.id === snap.pathId);
  //           const existingPt = snapPath?.points.find((x) => x.id === snap.pointId);

  //           if (curPath === 'sub' && snapPath?.type === 'main') { 
  //             const screenPt = map.project(snap.position);
  //             const result   = nearestOnPolyline2D(screenPt, snapPath.points, map);
  //             newPoint = makePoint(snap.position, {
  //               snapPathId: snap.pathId,
  //               snapT:      result.t,
  //             }); 
  //             if (localProgress !== null) shouldEnd = true;

  //           } else if (snapPath?.type === curPath) { 
  //             const isEndpoint = false

  //               // snapPath.points[0]?.id                               === snap.pointId ||
  //               // snapPath.points[snapPath.points.length - 1]?.id === snap.pointId;

  //             if (localProgress === null) { 
  //               newPoint = existingPt
  //                 ? { ...existingPt } 
  //                 : makePoint(snap.position); 

  //             } else if (isEndpoint) { 
  //               newPoint         = { id: snap.pointId, position: snap.position,
  //                                    pinId: null, snapPathId: null, snapT: null };
  //               localMergeTarget = { pathId: snap.pathId, pointId: snap.pointId };
  //               shouldEnd = true;

  //             } else { 
  //               newPoint  = existingPt ? { ...existingPt } : makePoint(snap.position);
  //               shouldEnd = true;
  //             }

  //           } else { 
  //             if (localProgress === null) { 
  //               newPoint = makePoint(snap.position); 
  //             } else { 
  //               newPoint  = existingPt ? { ...existingPt } : makePoint(snap.position);
  //               shouldEnd = true;
  //             }
  //           }

  //         } else if (snap.type === 'segment') {
  //           if (curPath === 'sub' && snap.pathType === 'main') { 
  //             newPoint = makePoint(snap.position, {
  //               snapPathId: snap.pathId,
  //               snapT:      snap.t,
  //             }); 
  //             if (localProgress !== null) shouldEnd = true;

  //           } else { 
  //             newPoint = makePoint(snap.position);
  //             dispatch(insertPointAtIndex({
  //               pathId: snap.pathId,
  //               index:  snap.segIndex + 1,
  //               point:  {
  //                 id:         newPoint.id,
  //                 position:   newPoint.position,
  //                 pinId:      null,
  //                 snapPathId: null,
  //                 snapT:      null,
  //               },
  //             })); 
  //             if (localProgress !== null) shouldEnd = true;
  //           }
  //         }
  //       } else {
  //         newPoint = makePoint([lng, lat]);
  //       }

  //       if (!localProgress) { 
  //         localProgress = { type: curPath, points: [newPoint] };
  //       } else {
  //         localProgress = { ...localProgress, points: [...localProgress.points, newPoint] };
  //       }
 
  //       dispatch(setInProgress(localProgress)); 
  //       pushPreview(localProgress, null);

  //       if (shouldEnd) {
  //         commitPath();
  //       }
  //     });
 
  //     on('dblclick', (e) => {
  //       e.preventDefault(); 
  //       if (localProgress && localProgress.points.length > 1) {
  //         localProgress = {
  //           ...localProgress,
  //           points: localProgress.points.slice(0, -1),
  //         };
  //       }
  //       commitPath();
  //     });
 
  //     on('mousemove', (e) => { 
  //       if (!localProgress) return;
  //       const mousePos = [e.lngLat.lng, e.lngLat.lat];
  //       pushPreview(localProgress, mousePos);
  //     });
 
  //     onKey('keydown', (e) => {
  //       if (e.key === 'Escape') cancelPath();
  //       if ((e.key === 'Delete' || e.key === 'Backspace') && !localProgress) {
  //         const { selectedPathId: sid } = stateRef.current;
  //         if (sid) dispatch(removePath(sid));
  //       }
  //     });
  //   }
 
  //   if (activeTool === 'select') {
  //     setCursor('default');
 
  //     const drag = { active: false };

  //     on('click', (e) => {
  //       if (drag.moved) { drag.moved = false; return; }

  //       dispatch(clearEraserSelectedPoints());

  //       const hitN = hitTestNode(e.point);
  //       if (hitN) {
  //         dispatch(clearNavSelection());
  //         return;
  //       }

  //       const pathId = hitTestPath(e.point);
  //       if (pathId) {
  //         dispatch(selectPath(pathId));
  //       } else {
  //         dispatch(clearNavSelection());
  //       }
  //     });

  //     on('mousedown', (e) => { 
  //       const hit = hitTestNode(e.point);
  //       if (hit) {
  //         map.dragPan.disable();
  //         setCursor('grabbing');

  //         const { paths: curPaths } = stateRef.current; 
  //         let snapPathId = null;
  //         for (const p of curPaths) {
  //           const pt = p.points.find((x) => x.id === hit.pointId);
  //           if (pt?.snapPathId) { snapPathId = pt.snapPathId; break; }
  //         }
 
  //         const exists = curPaths.some((p) =>
  //           p.points.some((pt) => pt.id === hit.pointId)
  //         );
  //         if (!exists) return;

  //         drag.active     = true;
  //         drag.moved      = false;
  //         drag.pathId     = hit.pathId;
  //         drag.pointId    = hit.pointId;
  //         drag.isSnap     = !!snapPathId;
  //         drag.snapPathId = snapPathId;
  //         return;
  //       }
  //     });

  //     on('mousemove', (e) => {
  //       if (!drag.active) { 
  //         const hitN = hitTestNode(e.point);
  //         if (hitN) { setCursor('grab'); return; }
  //         const hitP = hitTestPath(e.point);
  //         setCursor(hitP ? 'pointer' : 'default');
  //         return;
  //       }

  //       drag.moved = true;
  //       const { lng, lat } = e.lngLat;
  //       const mousePos = [lng, lat];

  //       const { paths: curPaths, selectedPathId: selPId, selectedPointId: selPtId } =
  //         stateRef.current;

  //       if (drag.isSnap) { 
  //         const mainPaths = curPaths.filter((p) => p.type === 'main');
  //         let bestResult  = null;
  //         let bestPathId  = null;
  //         let bestDistPx  = Infinity;

  //         for (const mp of mainPaths) {
  //           if (mp.points.length < 2) continue;
  //           const result = nearestOnPolyline2D(e.point, mp.points, map);
  //           if (result.distPx < bestDistPx) {
  //             bestDistPx = result.distPx;
  //             bestResult = result;
  //             bestPathId = mp.id;
  //           }
  //         }

  //         if (!bestResult || !bestPathId) return;
  //         const newPos = bestResult.position;
 
  //         drag.snapPathId = bestPathId;
 
  //         const updatedPaths = curPaths.map((p) => {
  //           if (!p.points.some((pt) => pt.id === drag.pointId)) return p;
  //           return {
  //             ...p,
  //             points: p.points.map((pt) =>
  //               pt.id === drag.pointId
  //                 ? { ...pt, position: newPos, snapT: bestResult.t, snapPathId: bestPathId }
  //                 : pt
  //             ),
  //           };
  //         });
  //         pushLines(updatedPaths, selPId);
  //         pushNodes(updatedPaths, selPId, selPtId);
  //         drag.livePos = newPos;
  //         drag.liveT   = bestResult.t;
  //       } else { 
  //         const movedPathIds = new Set(
  //           curPaths
  //             .filter((p) => p.points.some((pt) => pt.id === drag.pointId))
  //             .map((p) => p.id)
  //         );
  //         const partialPaths = curPaths.map((p) => {
  //           if (!movedPathIds.has(p.id)) return p;
  //           return {
  //             ...p,
  //             points: p.points.map((pt) =>
  //               pt.id === drag.pointId ? { ...pt, position: mousePos } : pt
  //             ),
  //           };
  //         });
 
  //         const updatedPaths = partialPaths.map((p) => {
  //           const hasAnchor = p.points.some(
  //             (pt) => pt.snapPathId && movedPathIds.has(pt.snapPathId)
  //           );
  //           if (!hasAnchor) return p;
  //           return {
  //             ...p,
  //             points: p.points.map((pt) => {
  //               if (!pt.snapPathId || !movedPathIds.has(pt.snapPathId)) return pt;
  //               const origHost = curPaths.find((hp) => hp.id === pt.snapPathId);
  //               const newHost  = partialPaths.find((hp) => hp.id === pt.snapPathId);
  //               if (!origHost || !newHost || newHost.points.length < 2) return pt;
  //               const { segIndex, localT } = decomposeSnapT(origHost.points, pt.snapT);
  //               const reanchored = reAnchorSnapPoint(newHost.points, segIndex, localT);
  //               return reanchored
  //                 ? { ...pt, position: reanchored.position, snapT: reanchored.snapT }
  //                 : pt;
  //             }),
  //           };
  //         });

  //         pushLines(updatedPaths, selPId);
  //         pushNodes(updatedPaths, selPId, selPtId);
  //         drag.livePos = mousePos;
  //       }
  //     });

  //     on('mouseup', () => {
  //       if (!drag.active) return;
  //       map.dragPan.enable();
  //       setCursor('default');

  //       if (drag.moved && drag.livePos) { 
  //         const { paths: finalPaths } = stateRef.current;
  //         const pathsWithNode = finalPaths.filter((p) =>
  //           p.points.some((pt) => pt.id === drag.pointId)
  //         );

  //         if (drag.isSnap && drag.snapPathId) { 
  //           pathsWithNode.forEach((p) =>
  //             dispatch(updateSnapPoint({
  //               pathId:     p.id,
  //               pointId:    drag.pointId,
  //               position:   drag.livePos,
  //               snapT:      drag.liveT,
  //               snapPathId: drag.snapPathId,
  //             }))
  //           );
  //         } else { 
  //           pathsWithNode.forEach((p) =>
  //             dispatch(updatePoint({
  //               pathId:   p.id,
  //               pointId:  drag.pointId,
  //               position: drag.livePos,
  //             }))
  //           );
 
  //           const movedPathIds = new Set(pathsWithNode.map((p) => p.id));
  //           finalPaths.forEach((p) => {
  //             p.points.forEach((pt) => {
  //               if (!pt.snapPathId || !movedPathIds.has(pt.snapPathId)) return;
  //               const origHost = finalPaths.find((hp) => hp.id === pt.snapPathId);
  //               const rawHost  = pathsWithNode.find((hp) => hp.id === pt.snapPathId);
  //               if (!origHost || !rawHost) return; 
  //               const simHostPoints = rawHost.points.map((hp) =>
  //                 hp.id === drag.pointId ? { ...hp, position: drag.livePos } : hp
  //               );
  //               const { segIndex, localT } = decomposeSnapT(origHost.points, pt.snapT);
  //               const reanchored = reAnchorSnapPoint(simHostPoints, segIndex, localT);
  //               if (!reanchored) return;
  //               dispatch(updateSnapPoint({
  //                 pathId:     p.id,
  //                 pointId:    pt.id,
  //                 position:   reanchored.position,
  //                 snapT:      reanchored.snapT,
  //                 snapPathId: pt.snapPathId,
  //               }));
  //             });
  //           });
  //         }
  //       }

  //       drag.active  = false; 
  //       drag.livePos = null;
  //       drag.liveT   = undefined;
  //     });
 
  //     onKey('keydown', (e) => {
  //       if (e.key === 'Delete' || e.key === 'Backspace') {
  //         const { selectedPathId: sid } = stateRef.current;
  //         if (sid) dispatch(removePath(sid));
  //       }
  //     });
  //   }
 
  //   if (activeTool === 'highlighter') {
  //     setCursor('crosshair');

  //     const drag = { active: false, moved: false, startPoint: null };
 
  //     const selBox = document.createElement('div');
  //     Object.assign(selBox.style, {
  //       position:       'absolute',
  //       border:         '1.5px dashed #e03131',
  //       background:     'rgba(224,49,49,0.08)',
  //       pointerEvents:  'none',
  //       display:        'none',
  //       zIndex:         '10',
  //       boxSizing:      'border-box',
  //     });
  //     const canvasContainer = map.getCanvasContainer();
  //     canvasContainer.appendChild(selBox);
  //     cleanups.push(() => selBox.remove());

  //     const updateSelBox = (sp, ep) => {
  //       selBox.style.left    = `${Math.min(sp.x, ep.x)}px`;
  //       selBox.style.top     = `${Math.min(sp.y, ep.y)}px`;
  //       selBox.style.width   = `${Math.abs(ep.x - sp.x)}px`;
  //       selBox.style.height  = `${Math.abs(ep.y - sp.y)}px`;
  //       selBox.style.display = 'block';
  //     };
 
  //     const handleMouseDown = (e) => {
  //       if (e.button !== 0) return;
  //       map.dragPan.disable(); 
  //       dispatch(clearEraserSelectedPoints());
  //       const rect = map.getCanvas().getBoundingClientRect();
  //       drag.active     = true;
  //       drag.moved      = false;
  //       drag.startPoint = { x: e.clientX - rect.left, y: e.clientY - rect.top };
  //     };
 
  //     const handleWindowMouseMove = (e) => {
  //       if (!drag.active) return;
  //       const rect  = map.getCanvas().getBoundingClientRect();
  //       const point = { x: e.clientX - rect.left, y: e.clientY - rect.top };
  //       const dx = Math.abs(point.x - drag.startPoint.x);
  //       const dy = Math.abs(point.y - drag.startPoint.y);
  //       if (dx > 4 || dy > 4) {
  //         drag.moved = true;
  //         updateSelBox(drag.startPoint, point);
  //       }
  //     };

  //     const handleWindowMouseUp = (e) => {
  //       if (!drag.active) return;
  //       map.dragPan.enable();
  //       drag.active          = false;
  //       selBox.style.display = 'none';

  //       if (drag.moved) {
  //         const rect = map.getCanvas().getBoundingClientRect();
  //         const ep   = { x: e.clientX - rect.left, y: e.clientY - rect.top };
  //         const sp   = drag.startPoint;
  //         const bbox = [
  //           [Math.min(sp.x, ep.x), Math.min(sp.y, ep.y)],
  //           [Math.max(sp.x, ep.x), Math.max(sp.y, ep.y)],
  //         ]; 
  //         const nodeFeatures = map.queryRenderedFeatures(bbox, {
  //           layers: [NAV_LAYERS.nodes, NAV_LAYERS.nodesSnap, NAV_LAYERS.nodesPin],
  //         });
  //         const selected = nodeFeatures
  //           .map((f) => ({ pointId: f.properties?.pointId, pathId: f.properties?.pathId }))
  //           .filter((p) => p.pointId && p.pathId); 
  //         const unique = [...new Map(selected.map((p) => [p.pointId, p])).values()];
 
  //         const { paths: curPaths } = stateRef.current;
  //         const seenPairs = new Set(unique.map((p) => `${p.pathId}:${p.pointId}`));
  //         const extraEntries = [];
  //         for (const { pointId } of unique) {
  //           for (const path of curPaths) {
  //             const key = `${path.id}:${pointId}`;
  //             if (seenPairs.has(key)) continue;
  //             if (path.points.some((pt) => pt.id === pointId)) {
  //               extraEntries.push({ pointId, pathId: path.id });
  //               seenPairs.add(key);
  //             }
  //           }
  //         }

  //         const allSelected = [...unique, ...extraEntries];
  //         if (allSelected.length > 0) dispatch(setEraserSelectedPoints(allSelected)); 
  //         setTimeout(() => { drag.moved = false; }, 10);
  //       }
  //     };

  //     const canvas = map.getCanvas();
  //     canvas.addEventListener('mousedown', handleMouseDown, true);
  //     window.addEventListener('mousemove', handleWindowMouseMove);
  //     window.addEventListener('mouseup',   handleWindowMouseUp);
  //     cleanups.push(() => {
  //       canvas.removeEventListener('mousedown', handleMouseDown, true);
  //       window.removeEventListener('mousemove', handleWindowMouseMove);
  //       window.removeEventListener('mouseup',   handleWindowMouseUp);
  //     });
 
  //     on('mousemove', (e) => {
  //       if (drag.active) return;
  //       const hitN = hitTestNode(e.point);
  //       if (hitN) { setCursor('crosshair'); return; }
  //       const hitP = hitTestPath(e.point);
  //       setCursor(hitP ? 'crosshair' : 'default');
  //     });
 
  //     on('click', (e) => {
  //       if (drag.moved) return;  
  //       const { paths: curPaths } = stateRef.current;

  //       const hitN = hitTestNode(e.point);
  //       if (hitN) {
  //         curPaths
  //           .filter((p) => p.points.some((pt) => pt.id === hitN.pointId))
  //           .forEach((p) =>
  //             dispatch(removePointFromPath({ pathId: p.id, pointId: hitN.pointId }))
  //           );
  //         return;
  //       }

  //       const pathId = hitTestPath(e.point);
  //       if (pathId) {
  //         const path = curPaths.find((p) => p.id === pathId);
 
  //         if (!path || path.points.length < 2) {
  //           dispatch(removePath(pathId));
  //         } else {
  //           const result = nearestOnPolyline2D(e.point, path.points, map);
  //           const segIdx = result.segIndex;
  //           const before = path.points.slice(0, segIdx + 1);  
  //           const after  = path.points.slice(segIdx + 1);     
  //           dispatch(removePath(pathId));
  //           if (before.length >= 2) {
  //             dispatch(addPath({ type: path.type, points: before, floorId: path.floorId }));
  //           }
  //           if (after.length >= 2) {
  //             dispatch(addPath({ type: path.type, points: after,  floorId: path.floorId }));
  //           }
  //         }
  //       }
  //     });
  //   }

  //   return () => {
  //     cleanups.forEach((fn) => fn());
  //     setCursor('default');
  //   };
  // }, [map, activeTool, activePath, shortestPath, dispatch, setCursor, hitTestPath, hitTestNode]);
 

  useEffect(() => {
    if (!map || !activeTool) return;

    const cleanups = [];
    const on = (evt, handler) => {
      map.on(evt, handler);
      cleanups.push(() => map.off(evt, handler));
    };
    const onKey = (evt, handler) => {
      window.addEventListener(evt, handler);
      cleanups.push(() => window.removeEventListener(evt, handler));
    };

    setCursor('default');
    dispatch(clearInProgress());
    pushPreview(null);

    if(shortestPath?.positions) return

    if (activeTool === 'pen') {
      setCursor('crosshair');

      let localProgress    = null;
      let localMergeTarget = null;

      const commitPath = () => {
        if (!localProgress || localProgress.points.length < 2) {
          localProgress    = null;
          localMergeTarget = null;
          dispatch(clearInProgress());
          pushPreview(null);
          return;
        }

        const mergeTarget = localMergeTarget;
        localMergeTarget  = null;

        if (mergeTarget) {
          const { paths: latestPaths } = stateRef.current;
          const targetPath = latestPaths.find((p) => p.id === mergeTarget.pathId);
          if (targetPath && targetPath.type === localProgress.type) {
            const tFirst = targetPath.points[0];
            const tLast  = targetPath.points[targetPath.points.length - 1];
            let mergedPoints = null;

            if (tFirst?.id === mergeTarget.pointId) {
              mergedPoints = [
                ...localProgress.points.slice(0, -1),
                ...targetPath.points,
              ];
            } else if (tLast?.id === mergeTarget.pointId) {
              mergedPoints = [
                ...localProgress.points.slice(0, -1),
                ...targetPath.points.slice().reverse(),
              ];
            }

            if (mergedPoints && mergedPoints.length >= 2) {
              dispatch(addPath({ type: localProgress.type, points: mergedPoints, floorId: stateRef.current.currentFloor?.enc_id }));
              dispatch(removePath(targetPath.id));
              localProgress = null;
              pushPreview(null);
              return;
            }
          }
        }

        dispatch(addPath({ type: localProgress.type, points: localProgress.points, floorId: stateRef.current.currentFloor?.enc_id }));
        localProgress = null;
        pushPreview(null);
      };

      const cancelPath = () => {
        localProgress = null;
        dispatch(clearInProgress());
        pushPreview(null);
        setCursor('crosshair');
      };

      on('click', (e) => {
        const { lng, lat } = e.lngLat;
        const { activePath: curPath, paths: curPaths } = stateRef.current;
 
        if (localProgress && localProgress.points.length >= 2) {
          const screenPt = map.project([lng, lat]);
          for (let i = 0; i < localProgress.points.length - 1; i++) {
            const pt  = localProgress.points[i];
            const ptPx = map.project(pt.position);
            const dx = screenPt.x - ptPx.x;
            const dy = screenPt.y - ptPx.y;
            if (Math.sqrt(dx * dx + dy * dy) < 20) {
              localProgress = {
                ...localProgress,
                points: [...localProgress.points, pt],
              };
              dispatch(setInProgress(localProgress));
              pushPreview(localProgress, null);
              commitPath();
              return;
            }
          }
        }

        const snap = findSnap({
          lngLat:         [lng, lat],
          map,
          paths:          curPaths,
          visiblePins:    visiblePins.current,
          pinThresholdPx: 25,
        });

        let newPoint;
        let shouldEnd    = false;
        localMergeTarget = null;

        if (snap) {
          if (snap.type === 'pin') {
            newPoint = makePoint(snap.position, { pinId: snap.pinId });
            if (localProgress !== null) shouldEnd = true;

          } else if (snap.type === 'node') {
            const snapPath   = curPaths.find((p) => p.id === snap.pathId);
            const existingPt = snapPath?.points.find((x) => x.id === snap.pointId);

            const isInProgressPoint = localProgress?.points.some(
              (pt) => pt.id === snap.pointId
            );

            if (isInProgressPoint && localProgress !== null) {
              const clickedIndex = localProgress.points.findIndex(
                (pt) => pt.id === snap.pointId
              );
              if (clickedIndex >= 0) {
                const loopPoint = localProgress.points[clickedIndex];
                localProgress = {
                  ...localProgress,
                  points: [...localProgress.points, loopPoint],
                };
              }
              dispatch(setInProgress(localProgress));
              pushPreview(localProgress, null);
              commitPath();
              return;
            }

            if (curPath === 'sub' && snapPath?.type === 'main') {
              const screenPt = map.project(snap.position);
              const result   = nearestOnPolyline2D(screenPt, snapPath.points, map);
              newPoint = makePoint(snap.position, {
                snapPathId: snap.pathId,
                snapT:      result.t,
              });
              if (localProgress !== null) shouldEnd = true;

            } else if (snapPath?.type === curPath) {
              const isEndpoint = false;
              // snapPath.points[0]?.id === snap.pointId ||
              // snapPath.points[snapPath.points.length - 1]?.id === snap.pointId;

              if (localProgress === null) {
                newPoint = existingPt
                  ? { ...existingPt }
                  : makePoint(snap.position);

              } else if (isEndpoint) {
                newPoint         = { id: snap.pointId, position: snap.position, pinId: null, snapPathId: null, snapT: null };
                localMergeTarget = { pathId: snap.pathId, pointId: snap.pointId };
                shouldEnd        = true;

              } else {
                newPoint  = existingPt ? { ...existingPt } : makePoint(snap.position);
                shouldEnd = true;
              }

            } else {
              if (localProgress === null) {
                newPoint = makePoint(snap.position);
              } else {
                newPoint  = existingPt ? { ...existingPt } : makePoint(snap.position);
                shouldEnd = true;
              }
            }

          } else if (snap.type === 'segment') { 
            if (curPath === 'sub' && snap.pathType === 'main') {
              newPoint = makePoint(snap.position, {
                snapPathId: snap.pathId,
                snapT:      snap.t,
              });
              if (localProgress !== null) shouldEnd = true;

            } else {
              newPoint = makePoint(snap.position);

              if (snap.pathId) {
                dispatch(insertPointAtIndex({
                  pathId: snap.pathId,
                  index:  snap.segIndex + 1,
                  point:  {
                    id:         newPoint.id,
                    position:   newPoint.position,
                    pinId:      null,
                    snapPathId: null,
                    snapT:      null,
                  },
                }));
              }

              if (localProgress !== null) shouldEnd = true;
            }
          }
        } else {
          newPoint = makePoint([lng, lat]);
        }

        if (!localProgress) {
          localProgress = { type: curPath, points: [newPoint] };
        } else {
          localProgress = { ...localProgress, points: [...localProgress.points, newPoint] };
        }

        dispatch(setInProgress(localProgress));
        pushPreview(localProgress, null);

        if (shouldEnd) {
          commitPath();
        }
      });

      on('dblclick', (e) => {
        e.preventDefault();
        if (localProgress && localProgress.points.length > 1) {
          localProgress = {
            ...localProgress,
            points: localProgress.points.slice(0, -1),
          };
        }
        commitPath();
      });

      on('mousemove', (e) => {
        if (!localProgress) return;
        const mousePos = [e.lngLat.lng, e.lngLat.lat];
        pushPreview(localProgress, mousePos);
      });

      onKey('keydown', (e) => {
        if (e.key === 'Escape') cancelPath();
        if ((e.key === 'Delete' || e.key === 'Backspace') && !localProgress) {
          const { selectedPathId: sid } = stateRef.current;
          if (sid) dispatch(removePath(sid));
        }
      });
    }

    if (activeTool === 'select') {
      setCursor('default');

      const drag = { active: false };

      on('click', (e) => {
        if (drag.moved) { drag.moved = false; return; }

        dispatch(clearEraserSelectedPoints());

        const hitN = hitTestNode(e.point);
        if (hitN) {
          dispatch(clearNavSelection());
          return;
        }

        const pathId = hitTestPath(e.point);
        if (pathId) {
          dispatch(selectPath(pathId));
        } else {
          dispatch(clearNavSelection());
        }
      });

      on('mousedown', (e) => {
        const hit = hitTestNode(e.point);
        if (hit) {
          map.dragPan.disable();
          setCursor('grabbing');

          const { paths: curPaths } = stateRef.current;
          let snapPathId = null;
          for (const p of curPaths) {
            const pt = p.points.find((x) => x.id === hit.pointId);
            if (pt?.snapPathId) { snapPathId = pt.snapPathId; break; }
          }

          const exists = curPaths.some((p) =>
            p.points.some((pt) => pt.id === hit.pointId)
          );
          if (!exists) return;

          drag.active     = true;
          drag.moved      = false;
          drag.pathId     = hit.pathId;
          drag.pointId    = hit.pointId;
          drag.isSnap     = !!snapPathId;
          drag.snapPathId = snapPathId;
          return;
        }
      });

      on('mousemove', (e) => {
        if (!drag.active) {
          const hitN = hitTestNode(e.point);
          if (hitN) { setCursor('grab'); return; }
          const hitP = hitTestPath(e.point);
          setCursor(hitP ? 'pointer' : 'default');
          return;
        }

        drag.moved = true;
        const { lng, lat } = e.lngLat;
        const mousePos = [lng, lat];

        const { paths: curPaths, selectedPathId: selPId, selectedPointId: selPtId } =
          stateRef.current;

        if (drag.isSnap) {
          const mainPaths = curPaths.filter((p) => p.type === 'main');
          let bestResult  = null;
          let bestPathId  = null;
          let bestDistPx  = Infinity;

          for (const mp of mainPaths) {
            if (mp.points.length < 2) continue;
            const result = nearestOnPolyline2D(e.point, mp.points, map);
            if (result.distPx < bestDistPx) {
              bestDistPx = result.distPx;
              bestResult = result;
              bestPathId = mp.id;
            }
          }

          if (!bestResult || !bestPathId) return;
          const newPos = bestResult.position;

          drag.snapPathId = bestPathId;

          const updatedPaths = curPaths.map((p) => {
            if (!p.points.some((pt) => pt.id === drag.pointId)) return p;
            return {
              ...p,
              points: p.points.map((pt) =>
                pt.id === drag.pointId
                  ? { ...pt, position: newPos, snapT: bestResult.t, snapPathId: bestPathId }
                  : pt
              ),
            };
          });
          pushLines(updatedPaths, selPId);
          pushNodes(updatedPaths, selPId, selPtId);
          drag.livePos = newPos;
          drag.liveT   = bestResult.t;

        } else {
          const movedPathIds = new Set(
            curPaths
              .filter((p) => p.points.some((pt) => pt.id === drag.pointId))
              .map((p) => p.id)
          );
          const partialPaths = curPaths.map((p) => {
            if (!movedPathIds.has(p.id)) return p;
            return {
              ...p,
              points: p.points.map((pt) =>
                pt.id === drag.pointId ? { ...pt, position: mousePos } : pt
              ),
            };
          });

          const updatedPaths = partialPaths.map((p) => {
            const hasAnchor = p.points.some(
              (pt) => pt.snapPathId && movedPathIds.has(pt.snapPathId)
            );
            if (!hasAnchor) return p;
            return {
              ...p,
              points: p.points.map((pt) => {
                if (!pt.snapPathId || !movedPathIds.has(pt.snapPathId)) return pt;
                const origHost = curPaths.find((hp) => hp.id === pt.snapPathId);
                const newHost  = partialPaths.find((hp) => hp.id === pt.snapPathId);
                if (!origHost || !newHost || newHost.points.length < 2) return pt;
                const { segIndex, localT } = decomposeSnapT(origHost.points, pt.snapT);
                const reanchored = reAnchorSnapPoint(newHost.points, segIndex, localT);
                return reanchored
                  ? { ...pt, position: reanchored.position, snapT: reanchored.snapT }
                  : pt;
              }),
            };
          });

          pushLines(updatedPaths, selPId);
          pushNodes(updatedPaths, selPId, selPtId);
          drag.livePos = mousePos;
        }
      });

      on('mouseup', () => {
        if (!drag.active) return;
        map.dragPan.enable();
        setCursor('default');

        if (drag.moved && drag.livePos) {
          const { paths: finalPaths } = stateRef.current;
          const pathsWithNode = finalPaths.filter((p) =>
            p.points.some((pt) => pt.id === drag.pointId)
          );

          if (drag.isSnap && drag.snapPathId) {
            pathsWithNode.forEach((p) =>
              dispatch(updateSnapPoint({
                pathId:     p.id,
                pointId:    drag.pointId,
                position:   drag.livePos,
                snapT:      drag.liveT,
                snapPathId: drag.snapPathId,
              }))
            );
          } else {
            pathsWithNode.forEach((p) =>
              dispatch(updatePoint({
                pathId:   p.id,
                pointId:  drag.pointId,
                position: drag.livePos,
              }))
            );

            const movedPathIds = new Set(pathsWithNode.map((p) => p.id));
            finalPaths.forEach((p) => {
              p.points.forEach((pt) => {
                if (!pt.snapPathId || !movedPathIds.has(pt.snapPathId)) return;
                const origHost = finalPaths.find((hp) => hp.id === pt.snapPathId);
                const rawHost  = pathsWithNode.find((hp) => hp.id === pt.snapPathId);
                if (!origHost || !rawHost) return;
                const simHostPoints = rawHost.points.map((hp) =>
                  hp.id === drag.pointId ? { ...hp, position: drag.livePos } : hp
                );
                const { segIndex, localT } = decomposeSnapT(origHost.points, pt.snapT);
                const reanchored = reAnchorSnapPoint(simHostPoints, segIndex, localT);
                if (!reanchored) return;
                dispatch(updateSnapPoint({
                  pathId:     p.id,
                  pointId:    pt.id,
                  position:   reanchored.position,
                  snapT:      reanchored.snapT,
                  snapPathId: pt.snapPathId,
                }));
              });
            });
          }
        }

        drag.active  = false;
        drag.livePos = null;
        drag.liveT   = undefined;
      });

      onKey('keydown', (e) => {
        if (e.key === 'Delete' || e.key === 'Backspace') {
          const { selectedPathId: sid } = stateRef.current;
          if (sid) dispatch(removePath(sid));
        }
      });
    }

    if (activeTool === 'highlighter') {
      setCursor('crosshair');

      const drag = { active: false, moved: false, startPoint: null };

      const selBox = document.createElement('div');
      Object.assign(selBox.style, {
        position:      'absolute',
        border:        '1.5px dashed #e03131',
        background:    'rgba(224,49,49,0.08)',
        pointerEvents: 'none',
        display:       'none',
        zIndex:        '10',
        boxSizing:     'border-box',
      });
      const canvasContainer = map.getCanvasContainer();
      canvasContainer.appendChild(selBox);
      cleanups.push(() => selBox.remove());

      const updateSelBox = (sp, ep) => {
        selBox.style.left    = `${Math.min(sp.x, ep.x)}px`;
        selBox.style.top     = `${Math.min(sp.y, ep.y)}px`;
        selBox.style.width   = `${Math.abs(ep.x - sp.x)}px`;
        selBox.style.height  = `${Math.abs(ep.y - sp.y)}px`;
        selBox.style.display = 'block';
      };

      const handleMouseDown = (e) => {
        if (e.button !== 0) return;
        map.dragPan.disable();
        dispatch(clearEraserSelectedPoints());
        const rect      = map.getCanvas().getBoundingClientRect();
        drag.active     = true;
        drag.moved      = false;
        drag.startPoint = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      };

      const handleWindowMouseMove = (e) => {
        if (!drag.active) return;
        const rect  = map.getCanvas().getBoundingClientRect();
        const point = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        const dx = Math.abs(point.x - drag.startPoint.x);
        const dy = Math.abs(point.y - drag.startPoint.y);
        if (dx > 4 || dy > 4) {
          drag.moved = true;
          updateSelBox(drag.startPoint, point);
        }
      };

      const handleWindowMouseUp = (e) => {
        if (!drag.active) return;
        map.dragPan.enable();
        drag.active          = false;
        selBox.style.display = 'none';

        if (drag.moved) {
          const rect = map.getCanvas().getBoundingClientRect();
          const ep   = { x: e.clientX - rect.left, y: e.clientY - rect.top };
          const sp   = drag.startPoint;
          const bbox = [
            [Math.min(sp.x, ep.x), Math.min(sp.y, ep.y)],
            [Math.max(sp.x, ep.x), Math.max(sp.y, ep.y)],
          ];
          const nodeFeatures = map.queryRenderedFeatures(bbox, {
            layers: [NAV_LAYERS.nodes, NAV_LAYERS.nodesSnap, NAV_LAYERS.nodesPin],
          });
          const selected = nodeFeatures
            .map((f) => ({ pointId: f.properties?.pointId, pathId: f.properties?.pathId }))
            .filter((p) => p.pointId && p.pathId);
          const unique = [...new Map(selected.map((p) => [p.pointId, p])).values()];

          const { paths: curPaths } = stateRef.current;
          const seenPairs   = new Set(unique.map((p) => `${p.pathId}:${p.pointId}`));
          const extraEntries = [];
          for (const { pointId } of unique) {
            for (const path of curPaths) {
              const key = `${path.id}:${pointId}`;
              if (seenPairs.has(key)) continue;
              if (path.points.some((pt) => pt.id === pointId)) {
                extraEntries.push({ pointId, pathId: path.id });
                seenPairs.add(key);
              }
            }
          }

          const allSelected = [...unique, ...extraEntries];
          if (allSelected.length > 0) dispatch(setEraserSelectedPoints(allSelected));
          setTimeout(() => { drag.moved = false; }, 10);
        }
      };

      const canvas = map.getCanvas();
      canvas.addEventListener('mousedown', handleMouseDown, true);
      window.addEventListener('mousemove', handleWindowMouseMove);
      window.addEventListener('mouseup',   handleWindowMouseUp);
      cleanups.push(() => {
        canvas.removeEventListener('mousedown', handleMouseDown, true);
        window.removeEventListener('mousemove', handleWindowMouseMove);
        window.removeEventListener('mouseup',   handleWindowMouseUp);
      });

      on('mousemove', (e) => {
        if (drag.active) return;
        const hitN = hitTestNode(e.point);
        if (hitN) { setCursor('crosshair'); return; }
        const hitP = hitTestPath(e.point);
        setCursor(hitP ? 'crosshair' : 'default');
      });

      on('click', (e) => {
        if (drag.moved) return;
        const { paths: curPaths } = stateRef.current;

        const hitN = hitTestNode(e.point);
        if (hitN) {
          curPaths
            .filter((p) => p.points.some((pt) => pt.id === hitN.pointId))
            .forEach((p) =>
              dispatch(removePointFromPath({ pathId: p.id, pointId: hitN.pointId }))
            );
          return;
        }

        const pathId = hitTestPath(e.point);
        if (pathId) {
          const path = curPaths.find((p) => p.id === pathId);

          if (!path || path.points.length < 2) {
            dispatch(removePathOnly(pathId));
          } else {
            const result = nearestOnPolyline2D(e.point, path.points, map);
            const segIdx = result.segIndex;
            const before = path.points.slice(0, segIdx + 1);
            const after  = path.points.slice(segIdx + 1);

            if (before.length >= 2 && after.length >= 2) {
              dispatch(updatePathPoints({ pathId, points: before }));
              dispatch(addPath({ type: path.type, points: after, floorId: path.floorId }));
            } else if (before.length >= 2) {
              dispatch(updatePathPoints({ pathId, points: before }));
            } else if (after.length >= 2) {
              dispatch(updatePathPoints({ pathId, points: after }));
            } else {
              dispatch(removePathOnly(pathId));
            }
          }
        }
      });
    }

    return () => {
      cleanups.forEach((fn) => fn());
      setCursor('default');
    };
  }, [map, activeTool, activePath, shortestPath, dispatch, setCursor, hitTestPath, hitTestNode]);


}
 
export function useAutoGenerateSubPaths() {
  const dispatch = useDispatch();
  const map      = useSelector((s) => s.map.mapContainer);
  const paths    = useSelector((s) => s.navigation.paths);
  const allPins  = useSelector((s) => s.api.allPins);
  const currentFloor = useSelector((s) => s.api.currentFloor);
  const activeTab    = useSelector((s) => s.api.activeTab);

  return useCallback(() => {
    if (!map) return;

    const { vertical, ...rest } = allPins || {};
 
    const regularPins = Object.values(rest).flat().filter((pin) => {
      if (!pin?.positions) return false;
      if (String(pin?.fp_id) !== String(currentFloor?.enc_id)) return false;
      if (activeTab !== 'all' && pin?.category !== activeTab) return false;
      return true;
    });
 
    const verticalPinsOnFloor = (vertical || []).flatMap((vt) => {
      const conn = vt.connections?.find(
        (c) => Number(c.fp_id) === Number(currentFloor?.enc_id)
      );
      if (!conn?.positions) return [];
      return [{
        enc_id:    vt.enc_id,
        positions: conn.positions,   
        fp_id:     currentFloor.enc_id,
        category:  'vertical',
      }];
    });

    const visiblePins = [...regularPins, ...verticalPinsOnFloor];

    const { newSubPaths } = autoGenerateSubPaths(paths, visiblePins, map, currentFloor);

    if (newSubPaths.length > 0) {
      dispatch(bulkAddPaths(newSubPaths));
    }
  }, [map, paths, allPins, currentFloor, activeTab, dispatch]);
}
 
export function useShortestPath() {
  const paths = useSelector((s) => s.navigation.paths);

  return useCallback((pinIdA, pinIdB) => {
    const nodeIdA = findPinNodeId(paths, pinIdA);
    const nodeIdB = findPinNodeId(paths, pinIdB);
    if (!nodeIdA || !nodeIdB) return null;

    const { nodes, adj } = buildNavGraph(paths);
    const result = dijkstra(nodes, adj, nodeIdA, nodeIdB);
    if (!result) return null;
 
    const positions = result.nodeIds.map((id) => nodes[id]).filter(Boolean);
    return { distanceM: result.distanceM, positions };
  }, [paths]);
}
